import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  createEvent,
  deleteEvent,
  duplicateEvent,
  getEventReferenceCodeSuggestion,
  getEvents,
  updateFeaturedEventOrder,
} from '@/lib/api';
import {
  EVENT_STATUSES,
  attendanceModeLabel,
  capacityLabel,
  eventDeleteImpact,
  eventStatusMeta,
  eventTitle,
  eventWhen,
  formatDate,
  isPastEvent,
  staffReferenceCodeErrors,
  suggestStaffReferenceCode,
} from '@/lib/eventStudio';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ROW_DENSITY_CLASSES,
  StatusPill,
  TableEmptyRow,
  TableFilterCard,
  adminInputClass,
  filterPillActiveClass,
  filterPillClass,
  tableCardClass,
  tableCellClass,
  tableHeadCellClass,
  tableHeaderRowClass,
  tableRowClass,
} from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';
import {
  ArrowDown, ArrowUp, CalendarDays, Copy, Mail, MoreHorizontal, Pencil, Plus, Search,
  Star, Trash2,
} from 'lucide-react';

const ALL = 'all';
const SKELETON_ROWS = 5;
const ROW_PADDING = ROW_DENSITY_CLASSES.comfortable;

const COLUMNS = [
  { id: 'title', label: 'Event', min: 220, grow: 1.8 },
  { id: 'when', label: 'When', min: 190, grow: 1.3 },
  { id: 'mode', label: 'Format', min: 110, grow: 0.8 },
  { id: 'registrations', label: 'Registered', min: 110, grow: 0.7 },
  { id: 'featured', label: 'Featured', min: 100, grow: 0.6 },
  { id: 'status', label: 'Status', min: 168, grow: 0.9 },
  { id: 'updated', label: 'Updated', min: 110, grow: 0.7 },
  { id: 'actions', label: '', min: 56, grow: 0.3 },
];
const GRID_TEMPLATE_COLUMNS = COLUMNS
  .map((column) => `minmax(${column.min}px, ${column.grow}fr)`).join(' ');
const TABLE_MIN_WIDTH = COLUMNS.reduce((total, column) => total + column.min, 0);

export default function AdminEvents() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // One dialog serves both "New event" and "Duplicate": a copy needs exactly
  // the same three answers, and `source` is what tells them apart.
  // { mode, source, name, date, code, edited, taken }
  const [newEvent, setNewEvent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setQ(searchInput.trim()), 350);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== ALL) params.status = statusFilter;
      if (featuredOnly) params.featured = true;
      if (q) params.q = q;
      const { data } = await getEvents(params);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total) || 0);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load events');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, featuredOnly, q]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  // Every event needs a staff reference code, so the New Event step asks for
  // the two things a good one is made of — what the event is, and when — and
  // suggests a code staff can then edit.
  const openNewEvent = () => setNewEvent({
    mode: 'create', source: null, name: '', date: '', code: '', edited: false, taken: false,
  });

  // A duplicate arrives with the name filled in and the date deliberately
  // empty: a copy is almost always the same event on a new day, and leaving the
  // old date in place is how two events end up claiming the same one.
  const openDuplicate = (event) => setNewEvent({
    mode: 'duplicate',
    source: event,
    name: `${eventTitle(event) || 'Untitled event'} (copy)`,
    date: '',
    code: suggestStaffReferenceCode(`${eventTitle(event) || 'event'} (copy)`),
    edited: false,
    taken: false,
  });

  const setNewEventField = (patch) => setNewEvent((current) => {
    const next = { ...current, ...patch, taken: false };
    if (!next.edited && (patch.name !== undefined || patch.date !== undefined)) {
      next.code = suggestStaffReferenceCode(next.name, next.date);
    }
    return next;
  });

  const newEventErrors = newEvent?.code ? staffReferenceCodeErrors(newEvent.code) : [];
  const canCreate = !!newEvent?.name.trim() && !!newEvent?.code && newEventErrors.length === 0;

  // A copy keeps the original's time of day, so moving it to a new date shifts
  // the whole schedule by whole days rather than nudging every time on it.
  const startAtForDraft = () => {
    if (!newEvent?.date) return null;
    const sourceTime = String(newEvent.source?.start_at || '').slice(11, 19);
    const time = newEvent.mode === 'duplicate' && /^\d{2}:\d{2}:\d{2}$/.test(sourceTime)
      ? sourceTime : '18:00:00';
    return `${newEvent.date}T${time}`;
  };

  const startNewEvent = async () => {
    if (creating || !canCreate) return;
    const duplicating = newEvent.mode === 'duplicate';
    try {
      setCreating(true);
      // Ask the server whether the code is free before creating anything, so a
      // clash is a question rather than a half-made event.
      const { data: check } = await getEventReferenceCodeSuggestion({
        name: newEvent.name, date: newEvent.date,
      });
      if (check?.suggestion === newEvent.code && check?.taken) {
        setNewEvent((current) => ({ ...current, taken: true }));
        return;
      }
      const startAt = startAtForDraft();
      // Either way this becomes a real draft, so the editor always has a record
      // to save against — no half-created state held in the browser.
      const { data } = duplicating
        ? await duplicateEvent(newEvent.source.id, {
          title: newEvent.name.trim(),
          staff_reference_code: newEvent.code,
          ...(startAt ? { start_at: startAt } : {}),
        })
        : await createEvent({
          translations: { en: { title: newEvent.name.trim() } },
          staff_reference_code: newEvent.code,
          ...(startAt ? { start_at: startAt } : {}),
        });
      toast.success(`${data.event_id} ${duplicating ? 'created from a copy' : 'created'}`);
      setNewEvent(null);
      navigate(`/admin/events/${data.id}`);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('already used')) {
        setNewEvent((current) => ({ ...current, taken: true }));
      }
      toast.error(
        Array.isArray(detail) ? detail.join(' ')
          : typeof detail === 'string' ? detail : 'Could not create the event',
      );
    } finally {
      setCreating(false);
    }
  };

  // Any event can go, at any status. What changes with its history is how loudly
  // the dialog warns first — the server refuses a consequential delete that did
  // not come through this confirmation.
  const removeEvent = async () => {
    if (!confirmDelete || deleting) return;
    try {
      setDeleting(true);
      const { data } = await deleteEvent(confirmDelete.id, { confirm: true });
      const unlinked = Number(data?.registrations_unlinked) || 0;
      toast.success(unlinked
        ? `Event deleted — ${unlinked} registration${unlinked === 1 ? '' : 's'} kept and unlinked`
        : 'Event deleted');
      setConfirmDelete(null);
      load();
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.join(' ')
        : typeof detail === 'string' ? detail : 'Could not delete this event');
    } finally {
      setDeleting(false);
    }
  };

  const featured = useMemo(
    () => items.filter((item) => item.featured)
      .sort((a, b) => (a.featured_priority || 999) - (b.featured_priority || 999)),
    [items],
  );

  const moveFeatured = async (eventId, direction) => {
    const order = featured.map((item) => item.id);
    const index = order.indexOf(eventId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= order.length) return;
    order.splice(target, 0, order.splice(index, 1)[0]);
    try {
      await updateFeaturedEventOrder(order.map((id, position) => ({
        id, featured_priority: position + 1,
      })));
      toast.success('Homepage order saved');
      load();
    } catch (error) {
      console.error(error);
      toast.error('Could not save the homepage order');
    }
  };

  const isDuplicating = newEvent?.mode === 'duplicate';
  const deleteImpact = eventDeleteImpact(confirmDelete);
  const hasActiveFilters = statusFilter !== ALL || featuredOnly || !!q;
  const headerCount = loading ? 'Loading…' : `${items.length} of ${total} event${total === 1 ? '' : 's'}`;

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <CalendarDays className="h-6 w-6 text-[#D4AF37]" />
            Events
          </h2>
          <p className="text-sm text-[#062B1A]/60">
            Everything the public events pages are built from. Draft an event, preview it, then
            publish when the page is ready.
          </p>
        </div>
        <Button
          onClick={openNewEvent}
          disabled={creating}
          className="bg-[#062B1A] text-white hover:bg-[#041B10]"
        >
          <Plus className="mr-2 h-4 w-4" />
          {creating ? 'Creating…' : 'New event'}
        </Button>
      </div>

      <Dialog open={!!newEvent} onOpenChange={(open) => { if (!open) setNewEvent(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="new-event-dialog">
          <DialogHeader>
            <DialogTitle>
              {isDuplicating ? 'Duplicate event' : 'New event'}
            </DialogTitle>
            <DialogDescription>
              {isDuplicating ? (
                <>
                  Event details and page content are copied into a new draft. It gets its own
                  reference code and web address with nobody registered. Uploaded brochures
                  stay with the original event.
                </>
              ) : (
                <>
                  Name the event and give it a date. The staff reference code is suggested from
                  those two — it is internal only and never appears on the website.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-event-name">Event name</Label>
              <Input
                id="new-event-name"
                value={newEvent?.name || ''}
                placeholder="FSL Info & Trial Session"
                onChange={(event) => setNewEventField({ name: event.target.value })}
                className={adminInputClass}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-event-date">Event date</Label>
              <Input
                id="new-event-date"
                type="date"
                value={newEvent?.date || ''}
                onChange={(event) => setNewEventField({ date: event.target.value })}
                className={adminInputClass}
              />
              {isDuplicating && (
                <p className="text-xs text-[#062B1A]/45">
                  The rest of the schedule — the end time and the registration window — moves
                  with this date and keeps its shape. Leave it empty to copy the dates as they are.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-event-code">Staff reference code</Label>
              <Input
                id="new-event-code"
                value={newEvent?.code || ''}
                placeholder="fsl-trial-2026-09-01"
                onChange={(event) => setNewEvent((current) => ({
                  ...current, code: event.target.value, edited: true, taken: false,
                }))}
                className={cn('font-mono', adminInputClass)}
              />
              <p className="text-xs text-[#062B1A]/45">
                A suggestion — edit it before saving. Staff only: it is never a URL, never the
                event ID, and never published.
              </p>
              {newEventErrors.map((message) => (
                <p key={message} className="text-xs text-[rgb(153,27,27)]">{message}</p>
              ))}
              {newEvent?.taken && (
                <p className="text-xs text-[rgb(153,27,27)]" data-testid="reference-code-taken">
                  Another event already uses this code. Choose a more specific one.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setNewEvent(null)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canCreate || creating}
              onClick={startNewEvent}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {creating ? 'Creating…' : isDuplicating ? 'Create the copy' : 'Create event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {featured.length > 0 && (
        <Card className="border border-[rgba(6,43,26,0.08)] p-4 shadow-sm">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
            Homepage order
          </p>
          <div className="space-y-2">
            {featured.map((event, index) => (
              <div
                key={event.id}
                data-testid="featured-row"
                className={cn(
                  'flex items-center gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] px-3 py-2',
                  // A finished event still holding a homepage slot is a thing to
                  // notice, so it reads as spent here too.
                  isPastEvent(event) && 'bg-[#FAFAFA]',
                )}
              >
                <Star className={cn('size-3.5 shrink-0', isPastEvent(event) ? 'text-[#062B1A]/25' : 'text-[#D4AF37]')} />
                <span className={cn(
                  'min-w-0 flex-1 truncate text-sm font-medium',
                  isPastEvent(event) ? 'text-[#062B1A]/45' : 'text-[#062B1A]',
                )}>
                  {eventTitle(event) || 'Untitled event'}
                  {isPastEvent(event) && (
                    <span className="ml-2 text-xs font-normal text-[#062B1A]/40">Past</span>
                  )}
                </span>
                <span className="text-xs text-[#062B1A]/45">#{index + 1}</span>
                <Button
                  type="button" size="icon" variant="ghost"
                  aria-label={`Move ${eventTitle(event)} up`}
                  disabled={index === 0}
                  onClick={() => moveFeatured(event.id, -1)}
                  className="h-7 w-7"
                >
                  <ArrowUp className="size-3.5" />
                </Button>
                <Button
                  type="button" size="icon" variant="ghost"
                  aria-label={`Move ${eventTitle(event)} down`}
                  disabled={index === featured.length - 1}
                  onClick={() => moveFeatured(event.id, 1)}
                  className="h-7 w-7"
                >
                  <ArrowDown className="size-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <TableFilterCard>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative max-w-xl flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
            <Input
              type="search"
              aria-label="Search events"
              placeholder="Search event title or public page..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={cn('pl-9', adminInputClass)}
            />
          </div>
          <div className="text-xs text-[#062B1A]/60">{headerCount}</div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button" variant="outline" size="sm"
            aria-pressed={statusFilter === ALL}
            onClick={() => setStatusFilter(ALL)}
            className={cn(filterPillClass, statusFilter === ALL && filterPillActiveClass)}
          >
            All
          </Button>
          {EVENT_STATUSES.map((status) => (
            <Button
              key={status}
              type="button" variant="outline" size="sm"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
              className={cn(filterPillClass, statusFilter === status && filterPillActiveClass)}
            >
              {eventStatusMeta(status).label}
            </Button>
          ))}
          <Button
            type="button" variant="outline" size="sm"
            aria-pressed={featuredOnly}
            onClick={() => setFeaturedOnly((current) => !current)}
            className={cn(filterPillClass, featuredOnly && filterPillActiveClass)}
          >
            Featured
          </Button>
          {hasActiveFilters && (
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => {
                setStatusFilter(ALL);
                setFeaturedOnly(false);
                setSearchInput('');
                setQ('');
              }}
              className="h-8 rounded-full px-3 text-xs text-[#062B1A]/65"
            >
              Clear filters
            </Button>
          )}
        </div>
      </TableFilterCard>

      <Card className={tableCardClass}>
        <Table style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
          <TableHeader>
            <TableRow className={cn('grid', tableHeaderRowClass)} style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}>
              {COLUMNS.map((column) => (
                <TableHead key={column.id} scope="col" className={cn(tableHeadCellClass, 'flex items-center')}>
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <TableRow
                  key={`skeleton-${index}`}
                  className={cn('grid', tableRowClass)}
                  style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                >
                  {COLUMNS.map((column) => (
                    <TableCell key={column.id} className={cn(tableCellClass, ROW_PADDING)}>
                      <div className="h-4 animate-pulse rounded bg-[#F1F3F5]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={COLUMNS.length}>
                {hasActiveFilters
                  ? 'No events match these filters.'
                  : 'No events yet. Create the first one to build its public page.'}
              </TableEmptyRow>
            ) : (
              items.map((event) => {
                const meta = eventStatusMeta(event.status);
                const cell = cn(tableCellClass, ROW_PADDING);
                // An event that has already happened stays in the list and stays
                // fully actionable — it is just spent, and reads that way, so
                // what is still ahead is what the eye lands on.
                const past = isPastEvent(event);
                const title = eventTitle(event) || 'Untitled event';
                const body = past ? 'text-[#062B1A]/40' : 'text-[#062B1A]/70';
                const registrations = Number(event.registration_count || 0);
                return (
                  <TableRow
                    key={event.id}
                    data-testid="event-row"
                    data-past={past ? 'true' : undefined}
                    onClick={() => navigate(`/admin/events/${event.id}`)}
                    className={cn(
                      'grid cursor-pointer', tableRowClass,
                      past && 'bg-[#FAFAFA] hover:bg-[#F1F3F5]',
                    )}
                    style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
                  >
                    <TableCell className={cn(cell, 'font-bold', past ? 'text-[#062B1A]/45' : 'text-[#062B1A]')}>
                      <span className="line-clamp-2 break-words">{title}</span>
                      {/* The live public address, not the internal slug — the
                          slug is technical detail and lives in the editor's
                          Audit section. */}
                      <span
                        data-testid="event-public-path"
                        className={cn(
                          'mt-0.5 block truncate text-[11px] font-normal',
                          past ? 'text-[#062B1A]/30' : 'text-[#062B1A]/45',
                        )}
                      >
                        {event.public_path || `/events/${event.slug}`}
                      </span>
                    </TableCell>
                    <TableCell className={cn(cell, body)}>{eventWhen(event)}</TableCell>
                    <TableCell className={cn(cell, body)}>
                      {attendanceModeLabel(event.attendance_mode)}
                    </TableCell>
                    <TableCell className={cn(cell, body)}>
                      <div className="flex items-center gap-1">
                        <span>{capacityLabel(event)}</span>
                        {registrations > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7', past ? 'text-[#062B1A]/35' : 'text-[#7A5A05]')}
                            aria-label={`Email attendees for ${title}`}
                            title="Email attendees"
                            onClick={(clickEvent) => {
                              clickEvent.stopPropagation();
                              navigate(`/admin/events/${event.id}/registrations`);
                            }}
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={cell}>
                      {event.featured
                        ? <StatusPill tone={past ? 'slate' : 'gold'}>#{event.featured_priority || '—'}</StatusPill>
                        : <span className="text-[#062B1A]/35">—</span>}
                    </TableCell>
                    <TableCell className={cell}>
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusPill tone={past ? 'slate' : meta.pillTone}>{meta.label}</StatusPill>
                        {/* Only where the status does not already say it. */}
                        {past && !['completed', 'archived'].includes(event.status) && (
                          <StatusPill tone="slate">Past</StatusPill>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={cn(cell, past ? 'text-[#062B1A]/35' : 'text-[#062B1A]/60')}>
                      {formatDate(event.updated_at)}
                    </TableCell>
                    <TableCell className={cn(cell, 'flex justify-end')}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7', past ? 'text-[#062B1A]/40' : 'text-[#062B1A]/70')}
                            aria-label={`Actions for ${title}`}
                            onClick={(clickEvent) => clickEvent.stopPropagation()}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onSelect={() => navigate(`/admin/events/${event.id}`)}>
                            <Pencil className="mr-2 size-4" />
                            Open the event
                          </DropdownMenuItem>
                          {registrations > 0 && (
                            <DropdownMenuItem
                              onSelect={() => navigate(`/admin/events/${event.id}/registrations`)}
                            >
                              <Mail className="mr-2 size-4" />
                              Email attendees
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onSelect={() => openDuplicate(event)}>
                            <Copy className="mr-2 size-4" />
                            Duplicate event
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onSelect={() => setConfirmDelete(event)}
                            className="text-[rgb(153,27,27)] focus:text-[rgb(153,27,27)]"
                          >
                            <Trash2 className="mr-2 size-4" />
                            Delete event
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle>Delete this event?</DialogTitle>
            <DialogDescription>
              “{eventTitle(confirmDelete) || 'Untitled event'}” and its brochures and agency
              links will be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteImpact.warnings.length > 0 && (
            <ul
              data-testid="delete-event-warnings"
              className="list-disc space-y-1 rounded-lg border border-[rgba(153,27,27,0.25)] bg-[rgba(254,242,242,0.9)] py-3 pl-8 pr-4 text-sm text-[rgb(153,27,27)]"
            >
              {deleteImpact.warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmDelete(null)}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
              Keep it
            </Button>
            <Button
              type="button"
              disabled={deleting}
              onClick={removeEvent}
              className="bg-[rgb(153,27,27)] text-white hover:bg-[rgb(127,22,22)]"
            >
              <Trash2 className="mr-2 size-4" />
              {deleting ? 'Deleting…' : 'Delete event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
